import StopIcon from '@assets/images/icons/stop.svg?react'
import {
  useCrawlerOpenJob,
  useCrawlerRemoveJob,
  useCrawlerStopJob,
} from '@components/App/CrawlerStatus'
import {
  IMPORT_PROJECT,
  IMPORT_PROJECT_CRAWLER_ERROR,
  MODAL_NONE,
  MODAL_REPORT_PROJECT,
} from '@components/App/Modal/constants'
import {
  CANCEL_CRAWLING,
  DISCARD_CRAWL,
  STOP_CRAWLING,
} from '@components/App/SystemDialogs/constants'
import { useCrawlRedirect } from '@components/CrawlRedirectDialog'
import { SITEMAP_REPORT } from '@components/Router/constants'
import { Tooltip } from '@lib/components'
import { Info } from '@lib/components/Info'
import IconX from '@lib/icons/IconX'
import { Button } from '@octopus-do/octopus-storybook'
import { OctopusMonoLogo } from '@octopus-do/octopus-storybook/icons'
import { selectCrawlImportPreparing } from '@store/crawlImport'
import { closeModal, openModal, openSystemDialog } from '@storeOld/reducers/ui'
import cx from 'classnames'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { generatePath, useNavigate } from 'react-router-dom'
import { useKey, useLatest, useUnmount } from 'react-use'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { useAuth, useCrawler, useFeatureFlags } from '@/hooks'
import { NestClient } from '@/nest'
import {
  type AppState,
  type ImportSitemapData,
  ImportSitemapResponseTypeUrlset,
  type PageNameSource,
} from '@/types'
import type { ModalProps } from '@/types/modal'
import css from '../../../../Import.module.scss'
import { actionUrls, TextWithAction } from '../../../TextWithAction'
import { CrawlerLoadingFrame, type FrameSize } from './CrawlerLoadingFrame'
import { buildCrawlUrl, redirectExclusions } from './utils'

type Props = ModalProps & {
  website: string
  originalWebsite?: string
  metaTag: boolean
  // Infer page blocks and wireframes from the markup (OCT-4990).
  blocks?: boolean
  // Also the blocks' text content.
  blocksContent?: boolean
  excludedUrls: string[]
  pageNames?: PageNameSource
  teamId?: string
  folderId?: string | null
  // Land the crawl in the project the user already has open.
  intoOpenProject?: boolean
  // Ingest this crawl as a sitemap report instead of building a visual
  // project. Set when the crawl was started from the "Sitemap audit" button.
  asReport?: boolean
  initialFrameSize?: FrameSize
}

const CrawlerLoadingRun: React.FC<
  Props & { onRedirect: (url: string) => void }
> = ({
  website,
  originalWebsite = website,
  onRedirect,
  metaTag,
  blocks = false,
  blocksContent = false,
  excludedUrls,
  pageNames = 'url',
  teamId,
  folderId,
  asReport,
  intoOpenProject = false,
  initialFrameSize,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id: userId } = useAuth()

  const { crawler: featureFlags } = useFeatureFlags()

  // One target for both endings of the crawl: Stop lands the result too.
  const crawlTarget = { teamId, folderId, intoOpenProject, pageNames }

  const { stopBackgroundJob, cancelBackgroundJob } =
    useCrawlerStopJob(crawlTarget)
  const { remove } = useCrawlerRemoveJob()
  const { openJob } = useCrawlerOpenJob(crawlTarget)
  const [stopped, setStopped] = useState(false)
  // A report the api refused to save. Kept, with the crawl it came from, so Retry sends
  // the same pages again instead of the user sitting through another crawl (OCT-5312).
  const [unsavedReport, setUnsavedReport] = useState<ImportSitemapData | null>(
    null
  )
  const [savingReport, setSavingReport] = useState(false)

  const endpoint = new URL(
    buildCrawlUrl({
      baseUrl: import.meta.env.VITE_GENERATOR_URL!,
      website,
      userId: userId || null,
      pro: featureFlags.userHasMaximumLimit,
      metaTag,
      blocks,
      blocksContent,
      excludedUrls: redirectExclusions(excludedUrls, originalWebsite, website),
    })
  )
  endpoint.searchParams.set('confirmRedirect', 'true')
  const url = endpoint.toString()
  const domain = new URL(website).hostname.replace(/^www\./, '')

  const {
    id,
    redirect,
    progress,
    result,
    issues,
    extra,
    error,
    done,
    close: closeSse,
  } = useCrawler({
    url,
    onStopped: () => {
      dispatch(closeModal())
    },
  })

  useCrawlRedirect(redirect, onRedirect, () => {
    dispatch(
      // @ts-expect-error Legacy modal action accepts props at runtime.
      openModal(asReport ? MODAL_REPORT_PROJECT : IMPORT_PROJECT, {
        initialUrl: originalWebsite,
        defaultUrl: originalWebsite,
        immediately: false,
        initialBlocks: blocks,
        initialBlocksContent: blocksContent,
        initialExcludedUrls: excludedUrls,
        initialPageNames: pageNames,
        intoOpenProject,
        teamId,
        folderId,
      })
    )
  })

  useUnmount(() => {
    closeSse?.()
  })

  // The pages are all in and the crawler is waiting for the model (OCT-5038):
  // say so, or a full counter with a frozen URL reads as a hang.
  const writingBlocks = progress.stage === 'blocks' && !done
  const blocksStageText = blocksContent
    ? 'AI is detecting blocks and writing their text, almost done…'
    : 'AI is detecting blocks, almost done…'

  // The crawl is over and its blocks are being parsed into the project (OCT-5101):
  // a counter that moves, where the modal used to look hung.
  const preparing = useSelector(selectCrawlImportPreparing)
  const preparingText =
    preparing && `Preparing blocks… ${preparing.done} of ${preparing.total}`
  const stageText =
    preparingText || (writingBlocks ? blocksStageText : progress.url)

  const handleUpgradeClick = () => {
    sendGtmEvent(
      GtmTriggerName.UPGRADE_CRAWLER,
      'Click on Upgrade to 3000 while crawling'
    )

    window.open(actionUrls.UPGRADE_URL, '_blank')
  }

  const confirmClosing = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(CANCEL_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const confirmStop = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(
        openSystemDialog(STOP_CRAWLING, {
          callback: resolve,
        })
      )
    })

  const createReport = (crawl: ImportSitemapData) => {
    if (id) {
      remove(id)
    }
    setSavingReport(true)

    NestClient.sitemapReport
      .create({
        crawl: {
          faviconUrl: crawl.extra?.icon ?? null,
          // A 404 or a redirect is a row in the report, though it never becomes
          // a page in a sitemap — so pages and issues go over as one list.
          pages: [...(crawl.data ?? []), ...(crawl.issues ?? [])],
          title: crawl.extra?.title ?? null,
          url: website,
        },
        ...(teamId ? { teamId } : {}),
      })
      .then(({ id: uuid }) => {
        sendGtmEvent(GtmTriggerName.REPORT_CREATE_COMPLETED)
        dispatch(closeModal())
        navigate(generatePath(SITEMAP_REPORT, { uuid }))
      })
      .catch(() => {
        // The window stays: closing it with a toast that fades on its own left an empty
        // dashboard and a crawl nobody could get back.
        setUnsavedReport(crawl)
        setSavingReport(false)
      })
  }

  useEffect(() => {
    if (done || stopped) {
      const params: ImportSitemapData = {
        type: ImportSitemapResponseTypeUrlset,
        data: result,
        details: progress.details,
        extra: extra!,
        issues,
        limitExceeded: progress.limitExceeded,
        timeout: progress.timeoutExceeded,
      }

      if (asReport) {
        createReport(params)

        return
      }

      openJob(params, id || undefined)
    }
  }, [done, stopped])

  const handleStop = async () => {
    if (id) {
      stopBackgroundJob(id)
      return
    }

    const confirmed = await confirmStop()

    if (!confirmed) {
      return
    }

    setStopped(true)
  }

  const confirmDiscard = (): Promise<boolean> =>
    new Promise((resolve) => {
      dispatch(openSystemDialog(DISCARD_CRAWL, { callback: resolve }))
    })

  const handleClose = async () => {
    // A report the api refused is still only in this window: closing it throws the
    // crawl away, so ask as Crawl complete does (OCT-5317).
    if (unsavedReport) {
      if (await confirmDiscard()) {
        dispatch(closeModal())
      }
      return
    }

    // The crawl is over: the job is finished and already removed, so there is
    // nothing to cancel and nothing to confirm. This is how the user leaves
    // when landing the result was refused (the project limit, for one).
    if (done || stopped) {
      dispatch(closeModal())
      return
    }

    if (id) {
      const confirmed = await cancelBackgroundJob(id)
      if (confirmed) {
        dispatch(closeModal())
      }
      return
    }

    const confirmed = await confirmClosing()
    if (confirmed) {
      dispatch(closeModal())
    }

    return false
  }

  // The window is locked, so Esc doesn't close it on its own; it goes through the same
  // question as the ×. While that question is up, Esc belongs to it: Esc means Cancel.
  const dialogOpen = useSelector<AppState, boolean>(
    (state) => state.ui.dialogType !== MODAL_NONE
  )
  const latest = useLatest({ dialogOpen, handleClose })
  useKey('Escape', () => {
    if (!latest.current.dialogOpen) {
      latest.current.handleClose()
    }
  })

  useEffect(() => {
    if (error) {
      if (id) {
        remove(id)
      }
      dispatch(closeModal())
      dispatch(
        // @ts-expect-error
        openModal(IMPORT_PROJECT_CRAWLER_ERROR, {
          isLocked: true,
          website,
          error,
        })
      )
    }
  }, [error])

  return (
    <CrawlerLoadingFrame initialSize={initialFrameSize}>
      <div className={css.heading}>
        <IconX className={css.close} onClick={handleClose} />
      </div>
      <div className={css.content}>
        <div className={css.loadingWrapper}>
          <div className={css.favicon}>
            {extra?.icon ? (
              <img src={extra.icon} alt="" className={css.faviconImage} />
            ) : (
              <OctopusMonoLogo className={css.faviconIcon} />
            )}
          </div>

          <div className={css.crawlingTitle}>{domain}</div>

          <p className={css.description}>
            {intoOpenProject
              ? 'Once completed, you will choose which pages to add.'
              : 'Once completed, you will be redirected to project.'}
          </p>

          <div className={css.progress}>
            <div
              className={css.progressBar}
              style={{
                width: `${progress.progressPercentage}%`,
              }}
            />
          </div>

          {progress.scanned > 0 && (
            <>
              <div className={css.progressInfo}>
                <div className={css.progressInfo}>
                  <div className={css.progressTitle}>
                    {writingBlocks || preparing
                      ? 'Pages crawled:'
                      : 'Crawling now:'}
                  </div>
                  <div
                    className={cx(css.count, {
                      [css.active]: progress.limitExceeded,
                    })}
                  >
                    {progress.scanned} of {progress.totalCount}
                  </div>
                </div>
                {!done && (
                  <Tooltip
                    position="top"
                    content="Stop crawling"
                    nodeClassName={css.stop}
                    onClick={handleStop}
                  >
                    <StopIcon />
                  </Tooltip>
                )}
              </div>
              <div className={css.scannedTitle}>{stageText}</div>
            </>
          )}

          {unsavedReport && (
            <div className={css.error}>
              <span className={css.errorText}>
                The report couldn't be saved.
              </span>
              <Button
                className={css.errorButton}
                label="Retry"
                loading={savingReport}
                onClick={() => createReport(unsavedReport)}
              />
            </div>
          )}

          {progress.limitExceeded && (
            <Info className={css.info}>
              {featureFlags.userHasMaximumLimit ? (
                <TextWithAction
                  actionText="Help"
                  actionUrl={actionUrls.HELP_URL}
                >
                  Limited to first 3000 pages.
                </TextWithAction>
              ) : (
                <TextWithAction
                  actionText="Upgrade to 3000"
                  actionFn={handleUpgradeClick}
                >
                  Limited to {blocks ? 150 : 300} pages.
                </TextWithAction>
              )}
            </Info>
          )}
        </div>
      </div>
    </CrawlerLoadingFrame>
  )
}

const CrawlerLoading: React.FC<Props> = (props) => {
  const [website, setWebsite] = useState(props.website)

  return (
    <CrawlerLoadingRun
      {...props}
      key={website}
      website={website}
      originalWebsite={props.originalWebsite ?? props.website}
      onRedirect={setWebsite}
    />
  )
}

export default CrawlerLoading
